import React, { useState, useEffect } from 'react';
import SEOPreviewCard from './SEOPreviewCard';
import HelpButton from '../Admin/HelpButton';

interface PageInfo {
  path: string;
  name: string;
  icon: string;
  currentTitle?: string;
  currentDescription?: string;
  currentImage?: string;
}

interface PageSEOEditorProps {
  onPageSelect?: (path: string) => void;
}

const PageSEOEditor: React.FC<PageSEOEditorProps> = ({ onPageSelect }) => {
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [seoSettings, setSeoSettings] = useState<any>({});

  useEffect(() => {
    // Load SEO settings
    try {
      const raw = localStorage.getItem('seo_settings');
      if (raw) {
        const parsed = JSON.parse(raw);
        setSeoSettings(parsed);
      }
    } catch (e) {
      // Ignore
    }

    // Initialize pages
    const initialPages: PageInfo[] = [
      { path: '/', name: 'Home', icon: 'fas fa-home' },
      { path: '/galleries', name: 'Galleries', icon: 'fas fa-images' },
      { path: '/blog', name: 'Blog', icon: 'fas fa-blog' },
      { path: '/services', name: 'Services', icon: 'fas fa-gift' },
      { path: '/about', name: 'About', icon: 'fas fa-user' },
      { path: '/contact', name: 'Contact', icon: 'fas fa-envelope' }
    ];

    // Load current SEO data for each page
    const pagesWithSEO = initialPages.map(page => {
      const pageSettings = seoSettings.perPage?.[page.path] || {};
      return {
        ...page,
        currentTitle: pageSettings.title,
        currentDescription: pageSettings.description,
        currentImage: pageSettings.image
      };
    });

    setPages(pagesWithSEO);
  }, []);

  const getSEOStatus = (page: PageInfo): 'complete' | 'partial' | 'incomplete' => {
    const hasTitle = !!page.currentTitle;
    const hasDescription = !!page.currentDescription;
    const hasImage = !!page.currentImage;

    if (hasTitle && hasDescription && hasImage) return 'complete';
    if (hasTitle || hasDescription) return 'partial';
    return 'incomplete';
  };

  const handlePageClick = (path: string) => {
    setSelectedPage(path);
    if (onPageSelect) {
      onPageSelect(path);
    }
  };

  const selectedPageData = pages.find(p => p.path === selectedPage);

  return (
    <div className="page-seo-editor">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <h4>Select a Page to Edit SEO</h4>
        <HelpButton section="seo-pages" tooltip="Page SEO Editor Help" variant="link" />
      </div>
      <div className="pages-grid">
        {pages.map(page => {
          const status = getSEOStatus(page);
          return (
            <div
              key={page.path}
              className={`page-card ${status}`}
              onClick={() => handlePageClick(page.path)}
            >
              <div className="page-card-header">
                <h3 className="page-card-title">
                  <i className={page.icon} style={{ marginRight: '0.5rem' }}></i>
                  {page.name}
                </h3>
                <span className={`seo-status-badge ${status}`}>
                  {status === 'complete' ? '✓ Complete' : status === 'partial' ? '⚠ Partial' : '✗ Incomplete'}
                </span>
              </div>
              <div className="page-card-preview">
                {page.currentTitle ? (
                  <>
                    <div className="page-card-preview-title">{page.currentTitle}</div>
                    <div>{page.currentDescription || 'No description'}</div>
                  </>
                ) : (
                  <div style={{ color: '#9ca3af' }}>No SEO configured</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedPageData && (
        <div style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white' }}>
          <h3>SEO Preview for {selectedPageData.name}</h3>
          <SEOPreviewCard
            title={selectedPageData.currentTitle || `${selectedPageData.name} - Carmen Lorraine Photography`}
            description={selectedPageData.currentDescription || `Visit my ${selectedPageData.name.toLowerCase()} page`}
            url={`${window.location.origin}${selectedPageData.path}`}
            image={selectedPageData.currentImage}
          />
        </div>
      )}
    </div>
  );
};

export default PageSEOEditor;

